import { useCallback, useEffect, useRef, useState } from 'react';

import { SiteState, useGetMicrostoreSitesPaginatedQuery } from 'data/__generated__';

import { DataTable } from 'components/DataTable';
import type { QuantityGenerationColumn } from 'pages/MicrostoreColumnGroups/Popups/QuantityGenerationPopup';

import { mcgSiteColumns, type McgSiteRow, type McgSiteFilterInput } from '../constants/mcgSiteColumns';

export const SiteList = ({
    onSelectedColumnsChange,
}: {
    onSelectedColumnsChange?: (columns: QuantityGenerationColumn[]) => void;
}) => {
    const defaultFilters: McgSiteFilterInput = {
        stateFilter: [SiteState.Regular],
    };

    const {
        loading: sitesLoading,
        data: sitesData,
        error: sitesError,
        refetch,
        fetchMore,
    } = useGetMicrostoreSitesPaginatedQuery({
        variables: { offset: 0, stateFilter: SiteState.Regular },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    // Selected sites can scroll out of `data`, so keep a running map of every site we have seen to
    // extract their columns even once they are no longer in the current page.
    const sitesByIdRef = useRef<Map<string, McgSiteRow>>(new Map());

    if (sitesError) {
        throw new Error('Une erreur est survenue lors de la récupération des sites');
    }

    const microstoreSites = sitesData?.microstoreSitesWithColumnGroupsFilter.sites ?? [];
    const hasMore = sitesData?.microstoreSitesWithColumnGroupsFilter.hasMore ?? false;

    for (const site of microstoreSites) {
        sitesByIdRef.current.set(site._id, site);
    }

    useEffect(() => {
        if (!onSelectedColumnsChange) {
            return;
        }
        const selectedColumns = selectedKeys
            .map((id) => sitesByIdRef.current.get(id))
            .filter((site): site is McgSiteRow => Boolean(site))
            .flatMap((site) =>
                site.microstoreColumnGroups.flatMap(({ columns }) =>
                    columns.map(({ _id, name }) => ({ _id, name, siteName: site.name })),
                ),
            );
        onSelectedColumnsChange(selectedColumns);
    }, [selectedKeys, onSelectedColumnsChange, microstoreSites]);

    const handleSelectionChange = useCallback((keys: string[]) => {
        setSelectedKeys(keys);
    }, []);

    return (
        <div className="flex h-full w-full flex-col overflow-hidden">
            <DataTable<McgSiteRow, McgSiteFilterInput>
                columns={mcgSiteColumns}
                data={microstoreSites}
                filterMode="server"
                getRowId={(row) => row._id}
                getRowLink={(row) => `/microstore-column-groups/${row._id}`}
                hasMore={hasMore}
                isLoading={sitesLoading}
                enableSelection
                selectedKeys={selectedKeys}
                onSelectionChange={handleSelectionChange}
                refetch={async (vars) => {
                    const stateArray = vars.filters?.stateFilter;
                    await refetch({
                        offset: 0,
                        filterString: vars.filters?.filterString,
                        stateFilter: stateArray?.[0],
                    });
                }}
                fetchMore={async (opts) => {
                    const stateArray = opts.variables.filters?.stateFilter;
                    await fetchMore({
                        variables: {
                            offset: opts.variables.offset ?? microstoreSites.length,
                            filterString: opts.variables.filters?.filterString,
                            stateFilter: stateArray?.[0],
                        },
                    });
                }}
                defaultFilters={defaultFilters}
                searchPlaceholder="Nom du site ou de l'organisation"
                searchFilterKey="filterString"
            />
        </div>
    );
};
